<div [id]="id" tabindex="-1" class="absolute top-0 left-0 right-0 z-50 hidden w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full" (click)="$event.stopPropagation()">
    <div class="relative w-[600px] max-h-full rounded-lg">
        <div class="relative primary-purple-light-2-background rounded-lg shadow dark:bg-gray-700 p-[25px]">
            <!-- Header -->
            <div class="flex items-center gap-4">
                <div class="flex justify-between items-center w-full">
                    <h3 class="text-xl font-bold primary-purple-text dark:text-gray-400">
                        {{ literalService.get('users.activePasses.title', true) }}
                    </h3>
                    @if (mode === 'edit' && editing && !confirmingDelete) {
                        <div class="primary-purple-border px-2 py-1 flex items-center border-solid border-[1px] rounded text-sm gap-1 cursor-pointer hover:bg-gray-100" (click)="requestDelete()">
                            <img [src]="images.services.delete" alt="delete" class="w-[18px] h-[18px]" />
                        </div>
                    }
                </div>
                <button (click)="close()" type="button" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white">
                    <img [src]="images.close" class="h-6 w-6" />
                </button>
            </div>

            <!-- List of existing active passes -->
            @if (mode === 'list') {
                <div class="mt-6">
                    @if (visiblePasses.length === 0 && !loading) {
                        <div class="text-base primary-purple-text font-normal py-4 text-center">
                            {{ literalService.get('users.activePasses.empty', true) }}
                        </div>
                    } @else {
                        <div class="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                            @for (pass of visiblePasses; track pass.id) {
                                <div class="active-pass-row flex items-center gap-3 bg-white rounded border px-3 py-2 cursor-pointer transition-colors hover:bg-gray-100 hover:primary-purple-border" (click)="startEdit(pass)">
                                    <div class="flex-1 flex flex-col">
                                        <label class="font-semibold text-base primary-purple-text cursor-pointer">{{ pass.temporalPassName }}</label>
                                        <label class="text-sm neutrals-grey-04-text cursor-pointer">
                                            {{ literalService.get('users.activePasses.expires', true) }}: {{ formatDate(pass.endDateTime) }}
                                            @if (pass.usages !== undefined && pass.usages !== null) {
                                                · {{ literalService.get('users.activePasses.usages', true) }}: {{ pass.usages }}
                                            }
                                        </label>
                                    </div>
                                    <img [src]="images.services.edit" alt="edit" class="h-5 w-5" />
                                </div>
                            }
                        </div>
                    }
                </div>

                <div class="flex items-center justify-end mt-8">
                    <button type="button" class="primary gap-4 px-6 py-2 border-2 font-semibold uppercase cursor-pointer text-white" (click)="startCreate()" [disabled]="loading">
                        {{ literalService.get('users.activePasses.new', true) }}
                    </button>
                </div>
            }

            <!-- Create form -->
            @if (mode === 'create') {
                <div class="mt-6 flex flex-col">
                    <label class="font-normal text-base primary-purple-text mb-[6px]">{{ literalService.get('users.activePasses.selectTemporalPass', true) }}</label>
                    <select2
                        class="block w-full active-pass-select2"
                        [placeholder]="literalService.get('users.activePasses.selectTemporalPassPlaceholder', true)"
                        [data]="getTemporalPassesForSelect()"
                        [value]="selectedTemporalPassId"
                        [templates]="{option: option}"
                        (update)="selectTemporalPass($event)"
                    >
                        <ng-template #option let-label="label">
                            <app-select2-option [label]="label" />
                        </ng-template>
                    </select2>
                </div>

                <div class="flex items-center mt-8 justify-center">
                    <button type="button" class="secondary mr-5 gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer" (click)="cancel()">
                        {{ literalService.get('cancel', true) }}
                    </button>
                    <button type="button" class="primary gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer text-white" [disabled]="!canSaveCreate() || loading" (click)="create()">
                        {{ literalService.get('save', true) }}
                    </button>
                </div>
            }

            <!-- Edit form -->
            @if (mode === 'edit' && editing) {
                <fieldset [disabled]="confirmingDelete">
                    <div class="mt-6 flex flex-col">
                        <label class="font-normal text-base primary-purple-text mb-[6px]">{{ literalService.get('users.activePasses.temporalPass', true) }}</label>
                        <input
                            type="text"
                            class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                            [value]="editing.temporalPassName || ''"
                            disabled
                        />
                    </div>
                    <div class="flex gap-4 mt-4">
                        <div class="flex flex-col flex-1">
                            <label class="font-normal text-base primary-purple-text mb-[6px]">{{ literalService.get('users.activePasses.endDateTime', true) }}</label>
                            <input
                                type="datetime-local"
                                class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                                [value]="editingEndDateTime"
                                (change)="updateEndDateTime($event)"
                            />
                        </div>
                        <div class="flex flex-col flex-1">
                            <label class="font-normal text-base primary-purple-text mb-[6px]">{{ literalService.get('users.activePasses.usages', true) }}</label>
                            <input
                                type="number"
                                min="0"
                                class="block w-full px-3 py-2 bg-white border rounded font-normal placeholder-slate-400 focus:outline-none"
                                [value]="editingUsages ?? ''"
                                (change)="updateUsages($event)"
                            />
                        </div>
                    </div>
                </fieldset>

                <!-- Inline delete confirmation -->
                @if (confirmingDelete) {
                    <div class="mt-8 pt-4">
                        <p class="text-base primary-purple-text font-semibold">
                            {{ literalService.get('users.activePasses.deleteConfirmText', true) }}
                        </p>
                    </div>
                    <div class="mt-8 flex items-center justify-center">
                        <button type="button" class="secondary mr-5 gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer" (click)="cancelDelete()">
                            {{ literalService.get('cancel', true) }}
                        </button>
                        <button type="button" class="primary gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer text-white" (click)="confirmDelete()" [disabled]="loading">
                            {{ literalService.get('confirm', true) }}
                        </button>
                    </div>
                } @else {
                    <div class="flex items-center mt-8 justify-center">
                        <button type="button" class="secondary mr-5 gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer" (click)="cancel()">
                            {{ literalService.get('cancel', true) }}
                        </button>
                        <button type="button" class="primary gap-4 p-2 border-2 font-semibold w-full uppercase cursor-pointer text-white" [disabled]="!canSaveEdit() || loading" (click)="save()">
                            {{ literalService.get('save', true) }}
                        </button>
                    </div>
                }
            }
        </div>
    </div>
</div>
